/**
 * チバリヨ - P3 メンバー管理画面
 *
 * 機能:
 *   - メンバー一覧(アクティブ / 退職)
 *   - 新規招待モーダル(氏lastName / 名firstName / email / role / jobTitle / joinDate / avatarColor)
 *   - 編集スライドオーバー(氏lastName / 名firstName / role / jobTitle / avatarColor)
 *   - 氏名は lastName/firstName で保持し、表示用 name=「氏　名」を併存させる（各画面・帳票の後方互換）
 *   - ドラッグ並び替え(order 更新)
 *   - 退職処理(active=false)
 *   - チバリヨ利用可否(chibariyoAccess): ウリカイ等のみ利用するメンバーは
 *     チバリヨにログインできないようにする (auth.js で遮断。未設定は利用可)
 *   - 完全削除: 退職済み かつ 未ログイン(uid=null) のメンバーのみ (Rules でも制限)
 *   - 招待文面コピー
 *
 * 重要:
 *   - members 更新と同時に userIndex も同期更新する(role / active)
 *   - 自分自身の role / active 変更は不可(UI でガード)
 *   - admin が 0人になる操作は不可(UI でガード)
 *   - email 変更は不可(編集 UI に出さない)
 */

import {
  collection,
  query,
  orderBy,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

import { auth, db } from './firebase-init.js';

// アバターカラーは config から取得するが、Phase 1-D まではフォールバックも保持
const FALLBACK_AVATAR_COLORS = [
  { id: 'blue',   label: '青',   text: '#0C447C', bg: '#E6F1FB' },
  { id: 'teal',   label: '緑',   text: '#0F6E56', bg: '#E1F5EE' },
  { id: 'amber',  label: '橙',   text: '#854F0B', bg: '#FAEEDA' },
  { id: 'purple', label: '紫',   text: '#3C3489', bg: '#EEEDFE' },
  { id: 'coral',  label: '珊瑚', text: '#A32D2D', bg: '#FCEBEB' },
  { id: 'slate',  label: '青灰', text: '#475569', bg: '#F1F5F9' },
  { id: 'rose',   label: '薔薇', text: '#9F1239', bg: '#FFE4E6' },
  { id: 'indigo', label: '藍',   text: '#3730A3', bg: '#E0E7FF' }
];

const ROLE_LABEL = { admin: '管理者', sales: '営業', viewer: '閲覧者' };
const ROLE_OPTIONS = [
  { value: 'admin', label: '管理者' },
  { value: 'sales', label: '営業' },
  { value: 'viewer', label: '閲覧者' }
];

// 画面状態
let avatarColors = FALLBACK_AVATAR_COLORS;
let members = [];          // 全メンバー(active/inactive 両方)
let currentMemberSession = null;  // 現在ログイン中のメンバー情報

// 氏・名 → 表示用フルネーム（半角スペース区切り）。空欄があっても破綻しない。
function combineName(lastName, firstName) {
  return [String(lastName || '').trim(), String(firstName || '').trim()].filter(Boolean).join(' ');
}
// 既存メンバーの氏名を氏/名に分解。lastName/firstName があればそれを優先し、
// 無ければ name を空白（半角/全角）で分割（移行用の暫定分割）。
function splitName(m) {
  if (m && (m.lastName != null || m.firstName != null)) {
    return { lastName: m.lastName || '', firstName: m.firstName || '' };
  }
  const parts = String((m && m.name) || '').trim().split(/[\s　]+/).filter(Boolean);
  if (parts.length >= 2) return { lastName: parts[0], firstName: parts.slice(1).join(' ') };
  return { lastName: (m && m.name) || '', firstName: '' };
}

// =====================================================================
// 公開関数: P3 画面を初期化・描画
// =====================================================================
export async function renderMembersPage(container, sessionMember) {
  currentMemberSession = sessionMember;

  container.innerHTML = `
    <div class="p3-header">
      <h1>メンバー管理</h1>
      <button id="p3-invite-btn" class="btn btn-primary">+ 新規招待</button>
    </div>
    <div id="p3-error" class="p3-error" style="display:none;"></div>
    <div id="p3-content" class="p3-content">
      <div class="p3-loading">読み込み中…</div>
    </div>
  `;

  document.getElementById('p3-invite-btn').addEventListener('click', openInviteModal);

  await loadAvatarColorsFromConfig();
  await loadAndRenderMembers();
}

// =====================================================================
// データロード
// =====================================================================
async function loadAvatarColorsFromConfig() {
  try {
    const snap = await getDoc(doc(db, 'config', 'system'));
    if (snap.exists() && Array.isArray(snap.data().avatarColors)) {
      avatarColors = snap.data().avatarColors;
    }
  } catch (err) {
    console.warn('[チバリヨ] avatarColors の取得失敗、フォールバックを使用:', err);
  }
}

async function loadAndRenderMembers() {
  try {
    const snap = await getDocs(query(collection(db, 'members'), orderBy('order', 'asc')));
    members = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    renderMemberList();
  } catch (err) {
    showError('メンバー一覧の取得に失敗しました: ' + err.message);
  }
}

// =====================================================================
// 一覧描画
// =====================================================================
function renderMemberList() {
  const content = document.getElementById('p3-content');
  const active = members.filter((m) => m.active !== false);
  const inactive = members.filter((m) => m.active === false);

  content.innerHTML = `
    <section class="p3-section">
      <h2 class="p3-section-title">アクティブメンバー (${active.length})</h2>
      <ul id="p3-active-list" class="p3-member-list">
        ${active.map(renderMemberRow).join('')}
      </ul>
      <div class="p3-hint">行をドラッグして並び替えできます。並び順は P1 ダッシュボード等で使用されます。</div>
    </section>

    ${inactive.length > 0 ? `
      <section class="p3-section">
        <h2 class="p3-section-title">退職メンバー (${inactive.length})</h2>
        <ul class="p3-member-list p3-inactive">
          ${inactive.map(renderMemberRow).join('')}
        </ul>
      </section>
    ` : ''}
  `;

  attachRowEvents();
  attachDragAndDrop();
}

function renderMemberRow(m) {
  const palette = findAvatarColor(m.avatarColor);
  const initial = (m.name || '?').slice(0, 1);
  const isMe = currentMemberSession && m.id === currentMemberSession.memberId;
  const inactive = m.active === false;

  return `
    <li class="p3-member-row ${inactive ? 'is-inactive' : ''}" data-member-id="${m.id}" draggable="${!inactive}">
      <span class="p3-drag-handle" title="ドラッグで並び替え">⋮⋮</span>
      <span class="p3-avatar" style="color:${palette.text};background:${palette.bg}">${escapeHtml(initial)}</span>
      <div class="p3-name-block">
        <div class="p3-name">${escapeHtml(m.name || '')}${isMe ? ' <span class="p3-me-badge">あなた</span>' : ''}${m.chibariyoAccess === false ? ' <span class="p3-noaccess-badge">チバリヨ利用なし</span>' : ''}</div>
        ${m.jobTitle ? `<div class="p3-jobtitle">${escapeHtml(m.jobTitle)}</div>` : ''}
      </div>
      <span class="p3-role role-${m.role}">${ROLE_LABEL[m.role] || m.role}</span>
      <span class="p3-email">${escapeHtml(m.email || '')}</span>
      <span class="p3-login-status">${m.uid ? '<span class="p3-status-ok">ログイン済</span>' : '<span class="p3-status-pending">未ログイン</span>'}</span>
      <button class="btn btn-ghost btn-sm p3-edit-btn" data-member-id="${m.id}">編集</button>
    </li>
  `;
}

function attachRowEvents() {
  document.querySelectorAll('.p3-edit-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.memberId;
      openEditModal(id);
    });
  });
}

// =====================================================================
// ドラッグ&ドロップ並び替え
// =====================================================================
function attachDragAndDrop() {
  const list = document.getElementById('p3-active-list');
  if (!list) return;

  let draggedEl = null;

  list.querySelectorAll('.p3-member-row[draggable="true"]').forEach((row) => {
    row.addEventListener('dragstart', (e) => {
      draggedEl = row;
      row.classList.add('p3-dragging');
      e.dataTransfer.effectAllowed = 'move';
    });
    row.addEventListener('dragend', () => {
      row.classList.remove('p3-dragging');
      draggedEl = null;
      list.querySelectorAll('.p3-drop-target').forEach((el) => el.classList.remove('p3-drop-target'));
    });
    row.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (!draggedEl || row === draggedEl) return;
      row.classList.add('p3-drop-target');
    });
    row.addEventListener('dragleave', () => {
      row.classList.remove('p3-drop-target');
    });
    row.addEventListener('drop', async (e) => {
      e.preventDefault();
      row.classList.remove('p3-drop-target');
      if (!draggedEl || row === draggedEl) return;

      // DOM 上で並び順を変更
      const rect = row.getBoundingClientRect();
      const insertBefore = (e.clientY - rect.top) < rect.height / 2;
      if (insertBefore) {
        row.parentNode.insertBefore(draggedEl, row);
      } else {
        row.parentNode.insertBefore(draggedEl, row.nextSibling);
      }

      // 新しい order を Firestore に反映
      await saveNewOrder();
    });
  });
}

async function saveNewOrder() {
  const rows = document.querySelectorAll('#p3-active-list .p3-member-row');
  const batch = writeBatch(db);
  const updates = [];

  rows.forEach((row, idx) => {
    const id = row.dataset.memberId;
    const newOrder = idx + 1;
    const m = members.find((x) => x.id === id);
    if (m && m.order !== newOrder) {
      batch.update(doc(db, 'members', id), { order: newOrder, updatedAt: serverTimestamp() });
      updates.push({ id, newOrder });
    }
  });

  if (updates.length === 0) return;

  try {
    await batch.commit();
    // ローカルの members を更新
    updates.forEach(({ id, newOrder }) => {
      const m = members.find((x) => x.id === id);
      if (m) m.order = newOrder;
    });
  } catch (err) {
    showError('並び順の保存に失敗しました: ' + err.message);
    // リストを再描画して元に戻す
    await loadAndRenderMembers();
  }
}

// =====================================================================
// 新規招待モーダル
// =====================================================================
function openInviteModal() {
  const modal = createModal('新規メンバー招待');
  const usedColors = new Set(members.filter((m) => m.active !== false).map((m) => m.avatarColor));

  modal.body.innerHTML = `
    <form id="p3-invite-form" class="p3-form">
      <label class="p3-field">
        <span class="p3-field-label">氏（姓） <span class="p3-required">*</span></span>
        <input name="lastName" type="text" required maxlength="15" placeholder="粟村">
      </label>

      <label class="p3-field">
        <span class="p3-field-label">名 <span class="p3-required">*</span></span>
        <input name="firstName" type="text" required maxlength="15" placeholder="聡">
      </label>

      <label class="p3-field">
        <span class="p3-field-label">メールアドレス <span class="p3-required">*</span></span>
        <input name="email" type="email" required placeholder="name@dotarcs.com">
        <span class="p3-field-hint">Google アカウントのメールアドレス、または受信可能なメールアドレス</span>
      </label>

      <label class="p3-field">
        <span class="p3-field-label">権限 <span class="p3-required">*</span></span>
        <select name="role" required>
          <option value="">選択してください</option>
          ${ROLE_OPTIONS.map((r) => `<option value="${r.value}">${r.label}</option>`).join('')}
        </select>
      </label>

      <label class="p3-field">
        <span class="p3-field-label">職務メモ</span>
        <input name="jobTitle" type="text" maxlength="40" placeholder="物流機器担当">
      </label>

      <label class="p3-field">
        <span class="p3-field-label">入社日</span>
        <input name="joinDate" type="date">
      </label>

      <fieldset class="p3-field p3-color-field">
        <legend class="p3-field-label">アバターカラー <span class="p3-required">*</span></legend>
        <div class="p3-color-grid">
          ${avatarColors.map((c) => `
            <label class="p3-color-option" data-color-id="${c.id}">
              <input type="radio" name="avatarColor" value="${c.id}" required>
              <span class="p3-color-chip" style="color:${c.text};background:${c.bg}">A</span>
              <span class="p3-color-label">${escapeHtml(c.label)}</span>
              ${usedColors.has(c.id) ? `<span class="p3-color-used">使用中</span>` : ''}
            </label>
          `).join('')}
        </div>
        <div id="p3-color-warning" class="p3-warning" style="display:none;">
          ⚠ この色は既に他のメンバーが使用中です。区別が難しくなる可能性があります。
        </div>
      </fieldset>

      <label class="p3-field p3-checkbox-field">
        <input name="chibariyoAccess" type="checkbox" checked>
        <span class="p3-checkbox-label">チバリヨの利用を許可する</span>
        <span class="p3-field-hint">ウリカイ等のみ利用するメンバーはチェックを外してください(チバリヨにログインできなくなります)</span>
      </label>

      <div class="p3-form-actions">
        <button type="button" class="btn btn-ghost" id="p3-invite-cancel">キャンセル</button>
        <button type="submit" class="btn btn-primary" id="p3-invite-submit">招待を作成</button>
      </div>
    </form>
  `;

  // カラー選択時に「使用中」警告
  modal.body.querySelectorAll('input[name="avatarColor"]').forEach((input) => {
    input.addEventListener('change', () => {
      const warn = document.getElementById('p3-color-warning');
      warn.style.display = usedColors.has(input.value) ? 'block' : 'none';
    });
  });

  document.getElementById('p3-invite-cancel').addEventListener('click', () => modal.close());
  document.getElementById('p3-invite-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    await handleInviteSubmit(modal);
  });
}

async function handleInviteSubmit(modal) {
  const form = document.getElementById('p3-invite-form');
  const submitBtn = document.getElementById('p3-invite-submit');
  submitBtn.disabled = true;

  const data = Object.fromEntries(new FormData(form).entries());
  const email = data.email.trim().toLowerCase();

  // 重複チェック(email)
  if (members.some((m) => (m.email || '').toLowerCase() === email)) {
    showError('このメールアドレスは既に登録されています。');
    submitBtn.disabled = false;
    return;
  }

  // order: 既存最大値 + 1
  const maxOrder = members.reduce((max, m) => Math.max(max, m.order || 0), 0);

  const lastName = (data.lastName || '').trim();
  const firstName = (data.firstName || '').trim();
  const newMember = {
    // name は氏名フル（氏　名）で保持。各画面・帳票は name を参照するため後方互換になる。
    name: combineName(lastName, firstName),
    lastName,
    firstName,
    email: email,
    role: data.role,
    active: true,
    // チェックボックスは checked のときのみ FormData に載る ('on')
    chibariyoAccess: data.chibariyoAccess === 'on',
    order: maxOrder + 1,
    uid: null,
    jobTitle: data.jobTitle ? data.jobTitle.trim() : '',
    joinDate: data.joinDate || null,
    inactiveAt: null,
    lastLoginAt: null,
    invitedAt: serverTimestamp(),
    invitedBy: auth.currentUser.uid,
    avatarColor: data.avatarColor
  };

  try {
    const docRef = await addDoc(collection(db, 'members'), newMember);
    modal.close();
    showInviteConfirmDialog(docRef.id, newMember);
    await loadAndRenderMembers();
  } catch (err) {
    showError('メンバーの追加に失敗しました: ' + err.message);
    submitBtn.disabled = false;
  }
}

function showInviteConfirmDialog(memberId, member) {
  // チバリヨ利用なしのメンバーはログイン案内が不要なので登録完了のみ表示
  if (member.chibariyoAccess === false) {
    const modal = createModal('登録完了');
    modal.body.innerHTML = `
      <p class="p3-invite-success">
        ✅ ${escapeHtml(member.name)}さん(${escapeHtml(member.email)})を登録しました。
      </p>
      <p>チバリヨの利用は許可されていないため、ログイン案内は不要です。</p>
      <div class="p3-form-actions">
        <button type="button" class="btn btn-primary" id="p3-invite-close">閉じる</button>
      </div>
    `;
    document.getElementById('p3-invite-close').addEventListener('click', () => modal.close());
    return;
  }

  const url = window.location.origin + '/';
  const template = `${member.name}さん

DotArcs 営業管理システム「チバリヨ」のアカウントを作成しました。
以下の URL からログインしてください:

  ${url}

ログイン方法は以下の2つから選べます:
- Google アカウント(${member.email})でログイン
- 上記メールアドレスにログインリンクを送信(パスワード不要)

ご不明な点があれば、粟村までご連絡ください。`;

  const modal = createModal('招待完了 - メンバーへの連絡');
  modal.body.innerHTML = `
    <p class="p3-invite-success">
      ✅ ${escapeHtml(member.name)}さん(${escapeHtml(member.email)})を招待しました。
    </p>
    <p>以下の文面をコピーして、メール / Slack 等でご本人にお送りください:</p>
    <textarea id="p3-invite-template" class="p3-template-text" rows="12" readonly>${escapeHtml(template)}</textarea>
    <div class="p3-form-actions">
      <button type="button" class="btn btn-ghost" id="p3-invite-close">閉じる</button>
      <button type="button" class="btn btn-primary" id="p3-invite-copy">クリップボードにコピー</button>
    </div>
  `;

  document.getElementById('p3-invite-close').addEventListener('click', () => modal.close());
  document.getElementById('p3-invite-copy').addEventListener('click', async () => {
    const textarea = document.getElementById('p3-invite-template');
    textarea.select();
    try {
      await navigator.clipboard.writeText(textarea.value);
      const btn = document.getElementById('p3-invite-copy');
      btn.textContent = '✓ コピーしました';
      setTimeout(() => { btn.textContent = 'クリップボードにコピー'; }, 2000);
    } catch (err) {
      // フォールバック
      document.execCommand('copy');
    }
  });
}


// =====================================================================
// 編集モーダル
// =====================================================================
function openEditModal(memberId) {
  const m = members.find((x) => x.id === memberId);
  if (!m) return;

  const isMe = currentMemberSession && m.id === currentMemberSession.memberId;
  const usedColors = new Set(
    members.filter((x) => x.active !== false && x.id !== memberId).map((x) => x.avatarColor)
  );
  const isInactive = m.active === false;

  const nm = splitName(m);
  const modal = createModal(`メンバー編集: ${m.name}`);
  modal.body.innerHTML = `
    <form id="p3-edit-form" class="p3-form">
      <label class="p3-field">
        <span class="p3-field-label">氏（姓） <span class="p3-required">*</span></span>
        <input name="lastName" type="text" required maxlength="15" value="${escapeAttr(nm.lastName)}">
      </label>

      <label class="p3-field">
        <span class="p3-field-label">名 <span class="p3-required">*</span></span>
        <input name="firstName" type="text" required maxlength="15" value="${escapeAttr(nm.firstName)}" placeholder="（名を入力）">
      </label>

      <div class="p3-field">
        <span class="p3-field-label">メールアドレス</span>
        <div class="p3-readonly-value">${escapeHtml(m.email || '')}</div>
        <span class="p3-field-hint">メールアドレスは変更できません(変更が必要な場合は退職処理後、新規招待してください)</span>
      </div>

      <label class="p3-field">
        <span class="p3-field-label">権限 <span class="p3-required">*</span></span>
        <select name="role" required ${isMe ? 'disabled' : ''}>
          ${ROLE_OPTIONS.map((r) => `<option value="${r.value}" ${m.role === r.value ? 'selected' : ''}>${r.label}</option>`).join('')}
        </select>
        ${isMe ? '<span class="p3-field-hint">自分自身の権限は変更できません</span>' : ''}
      </label>

      <label class="p3-field">
        <span class="p3-field-label">職務メモ</span>
        <input name="jobTitle" type="text" maxlength="40" value="${escapeAttr(m.jobTitle || '')}">
      </label>

      <label class="p3-field">
        <span class="p3-field-label">入社日</span>
        <input name="joinDate" type="date" value="${escapeAttr(m.joinDate || '')}">
      </label>

      <fieldset class="p3-field p3-color-field">
        <legend class="p3-field-label">アバターカラー <span class="p3-required">*</span></legend>
        <div class="p3-color-grid">
          ${avatarColors.map((c) => `
            <label class="p3-color-option">
              <input type="radio" name="avatarColor" value="${c.id}" ${m.avatarColor === c.id ? 'checked' : ''} required>
              <span class="p3-color-chip" style="color:${c.text};background:${c.bg}">A</span>
              <span class="p3-color-label">${escapeHtml(c.label)}</span>
              ${usedColors.has(c.id) ? `<span class="p3-color-used">使用中</span>` : ''}
            </label>
          `).join('')}
        </div>
      </fieldset>

      <label class="p3-field p3-checkbox-field">
        <input name="chibariyoAccess" type="checkbox" ${m.chibariyoAccess !== false ? 'checked' : ''} ${isMe ? 'disabled' : ''}>
        <span class="p3-checkbox-label">チバリヨの利用を許可する</span>
        ${isMe
          ? '<span class="p3-field-hint">自分自身のチバリヨ利用は変更できません</span>'
          : '<span class="p3-field-hint">チェックを外すとチバリヨにログインできなくなります(ウリカイ等の他アプリには影響しません)</span>'
        }
      </label>

      <div class="p3-form-actions p3-form-actions-edit">
        <div class="p3-form-actions-left">
          ${isInactive
            ? `<button type="button" class="btn btn-success" id="p3-reactivate-btn" ${isMe ? 'disabled' : ''}>復職させる</button>`
            : `<button type="button" class="btn btn-danger" id="p3-retire-btn" ${isMe ? 'disabled' : ''}>退職処理</button>`
          }
          ${isInactive && !m.uid
            ? `<button type="button" class="btn btn-danger" id="p3-delete-btn">完全に削除</button>`
            : ''
          }
        </div>
        <div class="p3-form-actions-right">
          <button type="button" class="btn btn-ghost" id="p3-edit-cancel">キャンセル</button>
          <button type="submit" class="btn btn-primary" id="p3-edit-submit">変更を保存</button>
        </div>
      </div>
    </form>
  `;

  document.getElementById('p3-edit-cancel').addEventListener('click', () => modal.close());
  document.getElementById('p3-edit-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    await handleEditSubmit(modal, m, isMe);
  });

  const retireBtn = document.getElementById('p3-retire-btn');
  if (retireBtn) {
    retireBtn.addEventListener('click', () => handleRetire(modal, m));
  }
  const reactivateBtn = document.getElementById('p3-reactivate-btn');
  if (reactivateBtn) {
    reactivateBtn.addEventListener('click', () => handleReactivate(modal, m));
  }
  const deleteBtn = document.getElementById('p3-delete-btn');
  if (deleteBtn) {
    deleteBtn.addEventListener('click', () => handleDelete(modal, m));
  }
}

async function handleEditSubmit(modal, original, isMe) {
  const form = document.getElementById('p3-edit-form');
  const submitBtn = document.getElementById('p3-edit-submit');
  submitBtn.disabled = true;

  const data = Object.fromEntries(new FormData(form).entries());

  const lastName = (data.lastName || '').trim();
  const firstName = (data.firstName || '').trim();
  const updates = {
    // name は氏名フル（氏　名）で保持（各画面・帳票の後方互換）
    name: combineName(lastName, firstName),
    lastName,
    firstName,
    jobTitle: data.jobTitle ? data.jobTitle.trim() : '',
    joinDate: data.joinDate || null,
    avatarColor: data.avatarColor,
    updatedAt: serverTimestamp()
  };

  // 自分以外ならチバリヨ利用可否も更新可 (disabled のチェックボックスは FormData に載らない)
  if (!isMe) {
    updates.chibariyoAccess = data.chibariyoAccess === 'on';
  }

  // 自分以外なら role も更新可
  let roleChanged = false;
  if (!isMe && data.role && data.role !== original.role) {
    // admin 0人化チェック
    if (original.role === 'admin' && data.role !== 'admin') {
      const otherAdmins = members.filter((m) => m.id !== original.id && m.active !== false && m.role === 'admin').length;
      if (otherAdmins === 0) {
        showError('最後の admin は権限変更できません。先に別のメンバーを admin に昇格させてください。');
        submitBtn.disabled = false;
        return;
      }
    }
    updates.role = data.role;
    roleChanged = true;
  }

  try {
    await updateDoc(doc(db, 'members', original.id), updates);

    // userIndex 同期(uid が紐付いている場合のみ)
    if (roleChanged && original.uid) {
      await updateDoc(doc(db, 'userIndex', original.uid), {
        role: data.role,
        updatedAt: serverTimestamp()
      });
    }

    modal.close();
    await loadAndRenderMembers();
  } catch (err) {
    showError('保存に失敗しました: ' + err.message);
    submitBtn.disabled = false;
  }
}

async function handleRetire(modal, m) {
  // admin 0人化チェック
  if (m.role === 'admin') {
    const otherAdmins = members.filter((x) => x.id !== m.id && x.active !== false && x.role === 'admin').length;
    if (otherAdmins === 0) {
      showError('最後の admin は退職処理できません。先に別のメンバーを admin に昇格させてください。');
      return;
    }
  }

  if (!window.confirm(`${m.name}さんを退職処理します。よろしいですか?\n\n- ログインできなくなります\n- 過去の日報・タスクは保持されます\n- 後で「復職」も可能です`)) {
    return;
  }

  try {
    await updateDoc(doc(db, 'members', m.id), {
      active: false,
      inactiveAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // userIndex も active=false に
    if (m.uid) {
      await updateDoc(doc(db, 'userIndex', m.uid), {
        active: false,
        updatedAt: serverTimestamp()
      });
    }

    modal.close();
    await loadAndRenderMembers();
  } catch (err) {
    showError('退職処理に失敗しました: ' + err.message);
  }
}

async function handleDelete(modal, m) {
  // Rules 側も「active=false かつ uid=null」のみ削除可。UI 側でも二重ガード
  if (m.active !== false || m.uid) {
    showError('退職済みかつ未ログインのメンバーのみ削除できます。');
    return;
  }

  if (!window.confirm(`${m.name}さん(${m.email})を完全に削除します。よろしいですか?\n\n- この操作は取り消せません\n- ログイン実績のない退職メンバーのみ削除できます`)) {
    return;
  }

  try {
    await deleteDoc(doc(db, 'members', m.id));
    modal.close();
    await loadAndRenderMembers();
  } catch (err) {
    showError('削除に失敗しました: ' + err.message);
  }
}

async function handleReactivate(modal, m) {
  if (!window.confirm(`${m.name}さんを復職させます。よろしいですか?`)) {
    return;
  }

  try {
    await updateDoc(doc(db, 'members', m.id), {
      active: true,
      inactiveAt: null,
      updatedAt: serverTimestamp()
    });

    if (m.uid) {
      await updateDoc(doc(db, 'userIndex', m.uid), {
        active: true,
        updatedAt: serverTimestamp()
      });
    }

    modal.close();
    await loadAndRenderMembers();
  } catch (err) {
    showError('復職処理に失敗しました: ' + err.message);
  }
}

// =====================================================================
// モーダルの共通処理
// =====================================================================
function createModal(title) {
  const overlay = document.createElement('div');
  overlay.className = 'p3-modal-overlay';
  overlay.innerHTML = `
    <div class="p3-modal">
      <div class="p3-modal-header">
        <h3>${escapeHtml(title)}</h3>
        <button class="p3-modal-close" type="button" aria-label="閉じる">×</button>
      </div>
      <div class="p3-modal-body"></div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => {
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  };
  overlay.querySelector('.p3-modal-close').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  return {
    body: overlay.querySelector('.p3-modal-body'),
    close
  };
}

// =====================================================================
// ユーティリティ
// =====================================================================
function findAvatarColor(id) {
  return avatarColors.find((c) => c.id === id) || avatarColors[avatarColors.length - 1];
}

function showError(msg) {
  const el = document.getElementById('p3-error');
  if (!el) return;
  el.textContent = msg;
  el.style.display = 'block';
  setTimeout(() => { el.style.display = 'none'; }, 6000);
}

function escapeHtml(str) {
  return String(str || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

function escapeAttr(str) {
  return escapeHtml(str);
}
